/**
 * @module UploadGateStatus
 * @purpose Tri-state chain-of-custody upload gate indicator, shared by CollectionTargetsTable
 *          and UploadReceiptModal's TargetPicker
 * @depends TFunction, lucide-react
 * @dependedOnBy CollectionTargetsTable (per-target row), UploadReceiptModal TargetPicker
 *
 * @rules
 * - Performance: pure presentational, no data-fetching
 * - State: derives its state entirely from the `{ evidenceReceived, hasReceipt }` props
 * - Errors: none -- this is a display-only indicator, not an action
 */

import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Clock, Lock } from 'lucide-react'

export type UploadGateState = 'gated' | 'awaitingUpload' | 'uploaded'

export interface UploadGateTarget {
  evidenceReceived: boolean
  hasReceipt: boolean
}

/**
 * Resolve the tri-state upload gate for a collection target.
 *
 * @behaviour `hasReceipt` always wins (covers legacy targets uploaded before
 *            this feature, even when `evidenceReceived` is false). Otherwise
 *            `evidenceReceived` decides between gated and awaiting-upload.
 */
export function getUploadGateState(target: UploadGateTarget): UploadGateState {
  if (target.hasReceipt) return 'uploaded'
  if (target.evidenceReceived) return 'awaitingUpload'
  return 'gated'
}

interface UploadGateStatusProps {
  target: UploadGateTarget
  t: TFunction<'cases'>
}

const GATED_BADGE_CLASS =
  'items-centre inline-flex gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/50 dark:text-amber-400'
const AWAITING_BADGE_CLASS =
  'items-centre inline-flex gap-1.5 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-950/50 dark:text-sky-400'

/**
 * Tri-state chain-of-custody gate indicator for the collection package upload.
 *
 * @behaviour Renders a greyed "receive first" warning when the target's evidence has not
 *            been chain-of-custody received, a "received -- awaiting upload" badge once
 *            received but not yet uploaded, and nothing once a receipt exists (uploaded --
 *            no gate shown, including legacy targets with `evidenceReceived=false`).
 * @errors None -- pure display component.
 */
export function UploadGateStatus({ target, t }: UploadGateStatusProps): ReactElement | null {
  const state = getUploadGateState(target)

  if (state === 'uploaded') return null

  if (state === 'gated') {
    return (
      <span className={GATED_BADGE_CLASS} role="status">
        <Lock className="h-3 w-3" />
        {t('detail.collections.uploadReceipt.receiveFirstWarning')}
      </span>
    )
  }

  return (
    <span className={AWAITING_BADGE_CLASS} role="status">
      <Clock className="h-3 w-3" />
      {t('detail.collections.uploadReceipt.awaitingUpload')}
    </span>
  )
}
